import {
  DataResourceDetail,
  DataResourceDetailRequirement,
  DetailRequirement,
  EngagementStatusBlock,
  EngagementTaskTrait,
  ResourceDataType,
  TaskDataResponse,
  TaskDataTraitStatus,
  TaskFieldDetails,
} from '@cibo/core'
import { propEq } from 'ramda'

/**
 * Picks the canonical value field from a detail object using the trait's detailValueKey.
 * Returns `{ [detailValueKey]: value }` when the value is defined, otherwise `{}`.
 * Use this in place of manual `input`/`result`/`value` conditional spreads.
 */
export function pickDetailValue(
  detail: object,
  detailValueKey: 'input' | 'result' | 'value' = 'result'
): Record<string, unknown> {
  const v = (detail as Record<string, unknown>)[detailValueKey]
  return v !== undefined ? { [detailValueKey]: v } : {}
}

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T|\s|$)/

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function adjustDetailDatesForYear(
  value: unknown,
  sourceYear: number,
  targetYear: number
): { adjusted: unknown; datesChanged: boolean; changedFields: string[] } {
  if (sourceYear === targetYear) return { adjusted: value, datesChanged: false, changedFields: [] }

  const delta = targetYear - sourceYear
  let changed = false
  const changedFields: string[] = []

  function traverseDetailStructure(valueAtCurrent: unknown, parentKey?: string): unknown {
    if (valueAtCurrent == null) return valueAtCurrent
    if (typeof valueAtCurrent === 'string' && DATE_REGEX.test(valueAtCurrent)) {
      const year = parseInt(valueAtCurrent.substring(0, 4), 10)
      const newYear = year + delta
      changed = true
      if (parentKey && !changedFields.includes(parentKey)) {
        changedFields.push(parentKey)
      }
      let suffix = valueAtCurrent.substring(4)
      if (suffix.startsWith('-02-29') && !isLeapYear(newYear)) {
        suffix = '-02-28' + suffix.substring(6)
      }
      return `${String(newYear).padStart(4, '0')}${suffix}`
    }
    if (Array.isArray(valueAtCurrent))
      return valueAtCurrent.map(item => traverseDetailStructure(item, parentKey))
    if (typeof valueAtCurrent === 'object') {
      const result: Record<string, unknown> = {}
      for (const [k, val] of Object.entries(valueAtCurrent as Record<string, unknown>)) {
        result[k] = traverseDetailStructure(val, k)
      }
      return result
    }
    return valueAtCurrent
  }

  const adjusted = traverseDetailStructure(value)
  return { adjusted, datesChanged: changed, changedFields }
}

export type TASK_DETAIL_KEY_NAME = 'userDetails' | 'wfDetails' | 'fieldDetails'

export const TASK_DETAIL_KEY: Record<ResourceDataType, TASK_DETAIL_KEY_NAME> = {
  user: 'userDetails',
  workflow: 'wfDetails',
  field: 'fieldDetails',
}

export class TaskDataModel
  implements
    Pick<
      TaskDataResponse,
      'detailRequirements' | 'status' | 'fieldDetails' | 'userDetails' | 'wfDetails'
    >
{
  detailRequirements: DataResourceDetailRequirement[]
  status: EngagementStatusBlock

  fieldDetails?: TaskFieldDetails[]
  userDetails?: DataResourceDetail[]
  wfDetails?: DataResourceDetail[]

  traits?: TaskDataTraitStatus[]

  constructor(data: TaskDataResponse) {
    this.detailRequirements = data.detailRequirements
    this.status = data.status
    this.fieldDetails = data.fieldDetails
    this.userDetails = data.userDetails
    this.wfDetails = data.wfDetails
    this.traits = data.traits
  }

  getDetail({
    traitConfig,
    detailRequirement,
    resourceId,
  }: {
    traitConfig: EngagementTaskTrait | TaskDataTraitStatus
    detailRequirement?: DetailRequirement
    resourceId?: string
  }) {
    const taskDataTypeDetails = this[TASK_DETAIL_KEY[traitConfig.dataType]]

    const resourceDetails =
      traitConfig.dataType === 'field'
        ? (taskDataTypeDetails as TaskFieldDetails[])?.find(propEq('resourceId', resourceId))
            ?.details
        : (taskDataTypeDetails as DataResourceDetail[])

    return resourceDetails?.find(detail => {
      return detail.traitId === traitConfig.traitId && detail.year === detailRequirement?.year
    })
  }

  getDetailValue({
    traitConfig,
    detailRequirement,
    resourceId,
  }: {
    traitConfig: EngagementTaskTrait | TaskDataTraitStatus
    detailRequirement?: DetailRequirement
    resourceId?: string
  }) {
    const detail = this.getDetail({ traitConfig, detailRequirement, resourceId })

    if (!detail) return undefined
    if (!traitConfig.detailValueKey) {
      throw new Error('detailValueKey is not defined')
    }

    return detail[traitConfig.detailValueKey]
  }

  getTraitStatus(traitId: string): TaskDataTraitStatus | undefined {
    return this.traits?.find(propEq('traitId', traitId))
  }
}
